import { useState, useCallback } from 'react';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { getGooglePlacesApiKey } from '../utils/googleMapsConfig';

const GOOGLE_PLACES_API_KEY = getGooglePlacesApiKey();

function parseCityStateFromComponents(components = []) {
  let city = '';
  let state = '';
  components.forEach((component) => {
    const types = component?.types || [];
    if (!city && (types.includes('locality') || types.includes('sublocality') || types.includes('postal_town') || types.includes('neighborhood'))) {
      city = component.long_name || '';
    } else if (types.includes('administrative_area_level_1')) {
      state = component.long_name || component.short_name || '';
    }
  });
  if (city && state) return `${city}, ${state}`;
  return city || state || '';
}

function parseZipStateFromComponents(components = []) {
  let zip = '';
  let state = '';
  let city = '';
  components.forEach((component) => {
    const types = component?.types || [];
    if (types.includes('postal_code')) zip = component.long_name || '';
    if (types.includes('administrative_area_level_1')) state = component.long_name || component.short_name || '';
    if (!city && (types.includes('locality') || types.includes('sublocality') || types.includes('postal_town') || types.includes('neighborhood'))) {
      city = component.long_name || '';
    }
  });
  if (zip && state) return `${zip}, ${state}`;
  if (city && state) return `${city}, ${state}`;
  return state || city || '';
}

export function useEventsLocationSearch({
  isLoggedIn,
  userState,
  locationSearchInput,
  setLocationSearchInput,
  submittedLocationSearch,
  setSubmittedLocationSearch,
  useCurrentLocation,
  setUseCurrentLocation,
  loadEvents,
  onNotify,
}) {
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [showResolvedLocation, setShowResolvedLocation] = useState(false);
  const hasPlacesApiKey = Boolean(GOOGLE_PLACES_API_KEY);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    setUseCurrentLocation(false);
    const nextSearch = locationSearchInput.trim();
    if (nextSearch === submittedLocationSearch) {
      loadEvents();
      return;
    }
    setSubmittedLocationSearch(nextSearch);
  }, [locationSearchInput, submittedLocationSearch, loadEvents, setUseCurrentLocation, setSubmittedLocationSearch]);

  const handleClearSearch = useCallback(() => {
    const hadSubmitted = Boolean(submittedLocationSearch) || useCurrentLocation;
    setLocationSearchInput('');
    setSelectedPlace(null);
    setUseCurrentLocation(false);
    setShowResolvedLocation(false);
    if (hadSubmitted) {
      setSubmittedLocationSearch('');
      return;
    }
    loadEvents();
  }, [submittedLocationSearch, useCurrentLocation, loadEvents, setLocationSearchInput, setSubmittedLocationSearch, setUseCurrentLocation]);

  const handlePlaceSelect = useCallback(async (place) => {
    setSelectedPlace(place);
    setShowResolvedLocation(false);
    if (!place) {
      setLocationSearchInput('');
      setSubmittedLocationSearch('');
      loadEvents();
      return;
    }
    try {
      const placeId = place?.value?.place_id;
      if (!placeId) {
        const label = place?.label || '';
        setUseCurrentLocation(false);
        setLocationSearchInput(label);
        setSubmittedLocationSearch(label);
        return;
      }
      const geocodeResults = await geocodeByPlaceId(placeId);
      const components = geocodeResults?.[0]?.address_components || [];
      const normalized = parseCityStateFromComponents(components) || place.label || '';
      setUseCurrentLocation(false);
      setLocationSearchInput(normalized);
      setSubmittedLocationSearch(normalized);
    } catch {
      const fallback = place?.label || '';
      setUseCurrentLocation(false);
      setLocationSearchInput(fallback);
      setSubmittedLocationSearch(fallback);
    }
  }, [loadEvents, setLocationSearchInput, setSubmittedLocationSearch, setUseCurrentLocation]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!isLoggedIn) {
      onNotify?.('Sign in to use your location.', 'info');
      return;
    }
    if (!navigator.geolocation) {
      onNotify?.('Geolocation is not supported.', 'warning');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          if (hasPlacesApiKey) {
            const zipUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coords.latitude},${coords.longitude}&result_type=postal_code&key=${GOOGLE_PLACES_API_KEY}`;
            const zipData = await (await fetch(zipUrl)).json();
            const zipResults = zipData?.results || [];
            if (zipResults.length > 0) {
              const normalized = parseZipStateFromComponents(zipResults[0]?.address_components || []);
              if (normalized) {
                setUseCurrentLocation(false);
                setShowResolvedLocation(true);
                setSelectedPlace({ label: normalized, value: {} });
                setLocationSearchInput(normalized);
                setSubmittedLocationSearch(normalized);
                setIsLocating(false);
                return;
              }
            }
            const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coords.latitude},${coords.longitude}&key=${GOOGLE_PLACES_API_KEY}`;
            const data = await (await fetch(url)).json();
            const results = data?.results || [];
            let bestComponents = null;
            for (let i = 0; i < results.length; i++) {
              const comp = results[i]?.address_components || [];
              if (comp.some((c) => (c?.types || []).includes('postal_code')) && comp.some((c) => (c?.types || []).includes('administrative_area_level_1'))) {
                bestComponents = comp;
                break;
              }
            }
            if (!bestComponents && results.length > 0) bestComponents = results[0]?.address_components || [];
            const normalized = bestComponents ? parseZipStateFromComponents(bestComponents) : '';
            if (normalized) {
              setUseCurrentLocation(false);
              setShowResolvedLocation(true);
              setSelectedPlace({ label: normalized, value: {} });
              setLocationSearchInput(normalized);
              setSubmittedLocationSearch(normalized);
              setIsLocating(false);
              return;
            }
          }
          const fallbackLabel = userState || 'My location';
          setShowResolvedLocation(true);
          setSelectedPlace({ label: fallbackLabel, value: {} });
          setLocationSearchInput(fallbackLabel);
          setSubmittedLocationSearch('');
          setUseCurrentLocation(true);
        } catch {
          const fallbackLabel = userState || 'My location';
          setShowResolvedLocation(true);
          setSelectedPlace({ label: fallbackLabel, value: {} });
          setLocationSearchInput(fallbackLabel);
          setSubmittedLocationSearch('');
          setUseCurrentLocation(true);
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setIsLocating(false);
        onNotify?.('Location unavailable.', 'warning');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  }, [hasPlacesApiKey, userState, isLoggedIn, onNotify, setLocationSearchInput, setSubmittedLocationSearch, setUseCurrentLocation]);

  return {
    hasPlacesApiKey,
    selectedPlace,
    setSelectedPlace,
    isLocating,
    showResolvedLocation,
    setShowResolvedLocation,
    handleSearchSubmit,
    handleClearSearch,
    handlePlaceSelect,
    handleUseCurrentLocation,
  };
}
